import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { PermissionModel } from '../models/permission.model';
import { LoginModel, LoginRequestUpdatePassword, RecoverPasswordModel } from '@models/login.model';
import { UserModel } from '@models/user.model';
import { CONSTANTS } from '@app/config/constants';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // properties
  private urlApi: string;
  private urlLogin: string;
  public headers: HttpHeaders;
  public identity: UserModel;
  public userToken: any;
  private userTokenAlt: any;
  public permissions: Array<PermissionModel>;
  public quotaStudy: boolean;
  public quotaReport: boolean;
  private urlInteraction: string;
  private interactionToken: string;
  private newLoginToken: string;

  constructor(private http: HttpClient) {  
    this.urlApi = environment.urlApi;
    this.urlLogin = environment.urlApiDev;
    this.urlInteraction = `${environment.urlApiDev}/newcovifactura`;
    this.interactionToken = environment.newCoviFacturaApiKey;
    this.newLoginToken = environment.newLoginApiKey;
    this.readToken();
    this.setPermisions();
    this.getIdentity();
  }

  login(user: LoginModel) {
    this.isAutenticated();
    const userIp = localStorage.getItem('ipAddress')
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set(
        "Authorization",
        `Basic ${btoa(this.newLoginToken)}`
      );
    const options = { params: LoginModel.toResponse(user, userIp), headers };
    const apiUrl = environment.postgres ? `${environment.urlApiDev}/nuevologin/auth` : this.urlApi;

    return this.http.get(apiUrl + '/acceso_v3', options).pipe(
      map((resp: any) => {
        // Expires
        const data = environment.postgres ? resp.data : resp;
        let expiraToken = moment(new Date());
        expiraToken = expiraToken.add(1,"hours");
        sessionStorage.setItem('expiresCF', expiraToken.toDate().getTime().toString());
        sessionStorage.setItem(
          'user',
          JSON.stringify({
            userEmail: user.username,
            token: data['TOKEN'],
            userInfo: data,
          })
        );
        this.saveToken(data['TOKEN']);
        this.saveTokenAlterno(data['TOKEN_ALTERNO']);
        return data;
      })
    );
  }

  getUserIpAddress() {
    return this.http.get('https://api.ipify.org?format=json').pipe(
      map((data: any) => {
        localStorage.setItem('ipAddress', data.ip);
      },
        error => {
          console.error('Error al obtener la IP:', error);
        }
      ));
  }

  recoverPassword(username: string, option: string = CONSTANTS.USERS.RECOVER) {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set(
        "Authorization",
        `Basic ${btoa(this.newLoginToken)}`
      );
    const apiUrl = environment.postgres ? `${environment.urlApiDev}/nuevologin/auth` : this.urlApi;

    const recoverPassword: RecoverPasswordModel = {
      username,
    };
    const options = {
      params: RecoverPasswordModel.toResponse(recoverPassword, option),
      headers,
    };

    return this.http.get(`${apiUrl}/envio_olvide_contrasena`, options);
  }

  getFamilyList() {
    return this.http.get(`${this.urlApi}/listado_parentesco`).pipe(
      map((resp: any) => {
        localStorage.setItem('familyList', JSON.stringify(resp.PARENTESCO));
        return resp;
      })
    );
  }

  getCareersList() {
    return this.http.get(`${this.urlApi}/listado_carreras`).pipe(
      map((resp: any) => {
        localStorage.setItem('careerList', JSON.stringify(resp.CARRERAS));
        return resp;
      })
    );
  }

  getPermissions() {
    this.getIdentity();
    const roleParams: any = {
      id_pais: 1,
      tipo_documento: this.identity.userInfo.documentType,
      documento: this.identity.userInfo.document,
      idrol: this.identity.userInfo.useRoler,
    };
    const headers = new HttpHeaders().set('token', environment.postgres ? this.userTokenAlt : this.userToken);
    const options = { params: roleParams, headers };
    return this.http.get(`${this.urlApi}/pr_rol_opciones_v2`, options).pipe(
      map((resp: any) => {
        sessionStorage.setItem('permissions', JSON.stringify(resp));
        this.permissions = resp.MENU.map((permission) =>
          PermissionModel.fromResponse(permission)
        );
        this.quotaStudy = resp.ESTUDIO_CUPO && resp.ESTUDIO_CUPO === 'SI';
        this.quotaReport = resp.REPORTE_CUPOS && resp.REPORTE_CUPOS === 'SI';
        return resp;
      })
    );
  }

  setPermisions() {
    const permissions = sessionStorage.getItem('permissions');
    if (permissions) {
      const parsedPermissions = JSON.parse(permissions);
      if ("MENU" in parsedPermissions) {
        this.permissions = parsedPermissions.MENU.map((permission) =>
          PermissionModel.fromResponse(permission)
        );
        this.quotaStudy =
          parsedPermissions.ESTUDIO_CUPO &&
          parsedPermissions.ESTUDIO_CUPO === 'SI';
        this.quotaReport =
          parsedPermissions.REPORTE_CUPOS &&
          parsedPermissions.REPORTE_CUPOS === 'SI';
      }
    }
  }

  getGlobalData() {
    return this.http.get(`${this.urlApi}/inicializar`).pipe(
      map((resp: any) => {
        localStorage.setItem('documents', JSON.stringify(resp));
        return resp;
      })
    );
  }

  private saveToken(token: string) {
    this.userToken = token;
    sessionStorage.setItem('tokenCF', token);
    sessionStorage.setItem('tokenDate', new Date().toDateString());
  }
  private saveTokenAlterno(token: string) {
    this.userTokenAlt = token;
    sessionStorage.setItem('tokenALT', token);
  }


  readToken() {
    if (sessionStorage.getItem('tokenCF')) {
      this.userToken = sessionStorage.getItem('tokenCF');
    } else {
      this.userToken = '';
    }
    return this.userToken;
  }

  

  logout() {
    sessionStorage.removeItem('tokenCF');
    sessionStorage.removeItem('tokenALT');
  }

  getIdentity() {
    if (!sessionStorage.getItem('user')) {
      return;
    }
    return (this.identity = UserModel.fromResponse(
      JSON.parse(sessionStorage.getItem('user'))
    ));
  }

  isAutenticated(): boolean {
    const today = new Date().getTime();
    const expira = Number(sessionStorage.getItem('expiresCF'));
    if (
      sessionStorage.getItem('tokenCF') === undefined ||
      sessionStorage.getItem('tokenCF') === null ||
      expira < today
    ) {
      return false;
    } else {
      return this.userToken;
    }
  }
  

  // Examples:
  post(user: any) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const options = { params: user, headers };
    const body = { test: 123 };
    this.http.post('url', body, options);
  }

  put(user: any) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const options = { params: user, headers };
    const body = { test: 123 };
    this.http.post('url', body, options);
  }

  saveInteractionDate(body: any) {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type':  'application/json',
      'Authorization': `Basic ${btoa(this.interactionToken)}`
   });
   const options = {
      headers,
   };

  return this.http.post(`${this.urlInteraction}/CoviFactura/interaccion/guardar_interaccion`, body, options)
  }

  forceUpdatePassword(forceUpdateRequest: LoginRequestUpdatePassword, user: LoginModel): Observable<any> {

    forceUpdateRequest.user = user.username;
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set(
        "Authorization",
        `Basic ${btoa(this.newLoginToken)}`
      );
   const options = { headers };
   return this.http.post(`${this.urlLogin}/nuevologin/migration/migrate_new_password`, forceUpdateRequest, options).pipe(
    map((resp: any) => {
      // Expires
      const data = resp.data;
      let expiraToken = moment(new Date());
      expiraToken = expiraToken.add(1,"hours");
      sessionStorage.setItem('expiresCF', expiraToken.toDate().getTime().toString());
      sessionStorage.setItem(
        'user',
        JSON.stringify({
          userEmail: user.username,
          token: data['TOKEN'],
          userInfo: data,
        })
      );
      this.saveToken(data['TOKEN']);
      this.saveTokenAlterno(data['TOKEN_ALTERNO']);
      return data;
    })
  );
  }

}
